import Grid from '@mui/material/Grid';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CommonInput } from 'sharedComponents/inputGroup';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { SECOND_SOURCE_INCOME } from '../LeadGenConstant';
import { Lead } from '../LeadGenInterface';
import { postLeadCreation } from 'store/LeadCreationState';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const SecondSourceOfIncome: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, onBack, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, watch, getValues } = useFormContext();

  // React Hook Form Watcher
  const secondSourceIncomeWatcher = watch('secondSourceIncome');

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { secondSourceIncome, secondSourceIncomeValue } = getValues();

    const secondSourceOfIncomeData: Lead = {
      leadSourceId: widgetData?.leadSourceId,
      isSubmit,
      leadDetails: {
        'Second Source Income': secondSourceIncome,
        ...mappedData,
      },
    };

    if (secondSourceIncome === 'yes') {
      if (secondSourceOfIncomeData.leadDetails) {
        secondSourceOfIncomeData.leadDetails['Second Source Income Value'] = secondSourceIncomeValue;
      }
    }
    dispatch(postLeadCreation({ data: secondSourceOfIncomeData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        onNext?.();
      }
    });
  };

  const backStep = async () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <Grid container justifyContent={{ xs: 'center' }}>
      <Grid item xs={12} lg={8}>
        <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.secondSourceIncome')}</h3>
        <InputRadio
          rules={formRequiredRules(errorMessage(t).radioError)}
          name={'secondSourceIncome'}
          control={control}
          options={SECOND_SOURCE_INCOME(t)}
          directionRow={false}
          triggerValidationForField={(name: string) => {
            trigger(name);
          }}
          itemPerRow={{
            xs: 12,
            sm: 6,
          }}
          radioVariant='outline'
        />

        {secondSourceIncomeWatcher === 'yes' && (
          <Grid container justifyContent={{ xs: 'center' }} mt={5}>
            <Grid item xs={12} sm={6} md={8}>
              <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.secondSourceIncome2')}</h3>
              <CommonInput
                name='secondSourceIncomeValue'
                control={control}
                label={t('leadGen.secondIncome')}
                placeholder={t('leadGen.secondIncome')}
                rules={formRequiredRules(errorMessage(t).secondIncomeRequired)}
                triggerValidationForField={trigger}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const SecondSourceOfIncomeStep = React.forwardRef(SecondSourceOfIncome);

export default SecondSourceOfIncomeStep;
